import { createTheme, ThemeOptions } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import componentsOverride from '@/shared/themes/overrides/index';
import Palette from '@/shared/themes/palette';
import CustomShadows from '@/shared/themes/shadows';
import Typography from '@/shared/themes/typography';
import zIndex from '@/shared/themes/zIndex';

// Ensure consistent font family string
const fontFamily = "'Somar Sans', Helvetica, Arial, sans-serif";

export const useRTLTheme = () => {
  const { i18n } = useTranslation();
  const theme = Palette();
  const themeTypography = Typography(fontFamily);
  const themeCustomShadows = useMemo(() => CustomShadows(theme), [theme]);

  // Determine direction based on current language
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';

  const themeOptions = useMemo(
    (): ThemeOptions => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 380,
          md: 768,
          lg: 992,
          xl: 1536
        }
      },
      spacing: 8,
      direction,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      typography: themeTypography,
      zIndex: zIndex(theme),
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '*': {
              fontFamily
            },
            body: {
              fontFamily,
              direction
            }
          }
        }
      }
    }),
    [theme, themeTypography, themeCustomShadows, direction]
  );

  const themes = createTheme(themeOptions);
  themes.components = {
    ...themes.components,
    ...componentsOverride(themes)
  };

  return themes;
};
